import { useMemo } from 'react';

import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  SCHEDULE_PREVIEW_RUN_COUNT,
  type SchedulePreviewRun,
  getLocalDateKey,
  getSchedulePreviewColumnHeaders,
} from '../utils/schedulePreviewRuns';
import { getBrowserTimezone } from '../utils/timezoneData';

interface ScheduleNextRunsPreviewProps {
  runs: SchedulePreviewRun[];
  /** Author / display timezone — used for headers and day banding. */
  displayTimeZone: string;
  title?: string;
}

export const ScheduleNextRunsPreview = ({
  runs,
  displayTimeZone,
  title = `Next ${SCHEDULE_PREVIEW_RUN_COUNT} runs`,
}: ScheduleNextRunsPreviewProps) => {
  const theme = useTheme();
  const viewerTimeZone = getBrowserTimezone();
  const showViewerColumn = runs.some((run) => run.viewerLabel != null);

  const columnHeaders = useMemo(
    () => getSchedulePreviewColumnHeaders(displayTimeZone, viewerTimeZone),
    [displayTimeZone, viewerTimeZone]
  );

  if (runs.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Table
        size="small"
        sx={{
          tableLayout: 'fixed',
          '& .MuiTableCell-root': {
            wordBreak: 'break-word',
            verticalAlign: 'top',
          },
        }}
      >
        <TableHead>
          <TableRow>
            {columnHeaders.map((header) => (
              <TableCell key={header} sx={{ fontWeight: 600, py: 0.75 }}>
                <Typography variant="caption" component="span" sx={{ fontWeight: 600 }}>
                  {header}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {runs.map((run, index) => {
            const prevDateKey =
              index > 0 ? getLocalDateKey(runs[index - 1].utc, displayTimeZone) : null;
            const dateKey = getLocalDateKey(run.utc, displayTimeZone);
            const isNewScheduleDay = prevDateKey != null && prevDateKey !== dateKey;

            return (
              <TableRow
                key={run.utc.getTime()}
                sx={isNewScheduleDay ? { bgcolor: theme.palette.action.selected } : undefined}
              >
                <TableCell>
                  <Typography variant="body2">{run.scheduleLabel}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{run.utcLabel}</Typography>
                </TableCell>
                {showViewerColumn ? (
                  <TableCell>
                    <Typography variant="body2">{run.viewerLabel ?? '-'}</Typography>
                  </TableCell>
                ) : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
